"use client"

import { useState, useMemo, useEffect } from "react"
import {
  Card,
  Button,
  Input,
  Select,
  Avatar,
  Modal,
  Form,
  Row,
  Col,
  Typography,
  Divider,
  Empty,
} from "antd"
import {
  SearchOutlined,
  BankOutlined,
  CalendarOutlined,
  TeamOutlined,
  LogoutOutlined,
  MessageOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons"
import "./style.scss"
import TitleCustom from "src/components/TitleCustom"
import MemberService from "src/services/MemberService"
import CB1 from "src/components/Modal/CB1"
import { SYSTEM_KEY } from "src/constants/constants"
import { useSelector } from "react-redux"
import { getListComboByKey } from "src/lib/utils"
import notice from "src/components/Notice"

const { Title, Text, Paragraph } = Typography
const { TextArea } = Input

const JoinedOrganizations = () => {
  const [associations, setAssociations] = useState([])
  const [loading, setLoading] = useState(false)
  const [filters, setFilters] = useState({
    search: "",
    status: "all",
    type: "all",
    sort: "name",
  })
  const [leaveModal, setLeaveModal] = useState({
    visible: false,
    association: null,
  })
  const [reasonModal, setReasonModal] = useState({
    visible: false,
    association: null,
  })
  const [form] = Form.useForm()
  const { listSystemKey } = useSelector(state => state.appGlobal)

  const associationTypeList = useMemo(
    () => getListComboByKey(SYSTEM_KEY.ASSOCIATION_TYPE, listSystemKey),
    [listSystemKey],
  )
  const logAssociationTypeList = useMemo(() => {
    const list = getListComboByKey(
      SYSTEM_KEY.LOG_USER_ASSOCIATION_TYPE,
      listSystemKey,
    )
    console.log("logAssociationTypeList:", list)
    return list
  }, [listSystemKey])

  const getStatusKey = code => {
    switch (Number(code)) {
      case 1:
        return "Active"
      case 2:
        return "Inactive"
      case 3:
        return "Pending"
      case 4:
        return "PendingLeave"
      case 5:
        return "Rejected"
      default:
        return "unknown"
    }
  }

  const getStatusConfig = status => {
    switch (status) {
      case "Active":
        return {
          color: "success",
          icon: <CheckCircleOutlined />,
          className: "status-active",
        }
      case "Pending":
        return {
          color: "warning",
          icon: <ClockCircleOutlined />,
          className: "status-pending",
        }
      case "PendingLeave":
        return {
          color: "warning",
          icon: <ClockCircleOutlined />,
          className: "status-pending",
        }
      case "Inactive":
        return {
          color: "error",
          icon: <CloseCircleOutlined />,
          className: "status-inactive",
        }
      case "Rejected":
        return {
          color: "default",
          icon: <CloseCircleOutlined />,
          className: "status-rejected",
        }
      default:
        return { color: "default", icon: null, className: "" }
    }
  }

  const mapSystemValue = (list, codeValue) =>
    list.find(item => Number(item.CodeValue) === Number(codeValue))
      ?.Description || "Chưa có thông tin"

  useEffect(() => {
    const fetchAssociations = async () => {
      setLoading(true)
      try {
        const res = await MemberService.GetLisTLogUserAssociation({
          PageSize: 50,
          CurrentPage: 1,
        })
        if (res?.isError || !res?.Object?.ListLogUserAssociation) {
          setAssociations([])
          return
        }
        const rawList = res.Object.ListLogUserAssociation || []

        const filteredUnique = rawList.filter(
          (item, index, self) =>
            index ===
            self.findIndex(
              t =>
                t.AssociationID === item.AssociationID && t.Type === item.Type,
            ),
        )
        const formatted = filteredUnique.map(item => ({
          id: item.AssociationID || "Chưa có thông tin",
          name: item.AssociationName || "Chưa có thông tin",
          typeCode: item.AssociationType === 1 ? "main" : "sub",
          typeLabel: mapSystemValue(associationTypeList, item.AssociationType),
          parentAssociation: item.ParentAssociation || "Chưa có thông tin",
          membershipDate: item.CreateDate
            ? new Date(item.CreateDate).toLocaleDateString("vi-VN")
            : "Chưa có thông tin",
          status: getStatusKey(item.Type),
          statusLabel: mapSystemValue(logAssociationTypeList, item.Type),
          avatar: item.Avatar || null,
          leaveReason: item.Note || null,
          ObjectID: item.ObjectID,
        }))
        setAssociations(formatted)
      } catch (error) {
        console.error("Error fetching associations:", error)
        setAssociations([])
      } finally {
        setLoading(false)
      }
    }
    fetchAssociations()
  }, [associationTypeList, logAssociationTypeList])

  // Filter, sort
  const filteredAssociations = useMemo(() => {
    const filtered = associations.filter(assoc => {
      const matchesSearch = assoc.name
        ?.toLowerCase()
        .includes(filters.search.trim().toLowerCase())
      const matchesStatus =
        filters.status === "all" ||
        assoc.status?.toLowerCase() === filters.status.toLowerCase()
      const matchesType =
        filters.type === "all" || assoc.typeCode === filters.type
      return matchesSearch && matchesStatus && matchesType
    })

    filtered.sort((a, b) => {
      switch (filters.sort) {
        case "name":
          return a.name.localeCompare(b.name)
        case "date":
          return (
            new Date(a.membershipDate.split("/").reverse().join("-")) -
            new Date(b.membershipDate.split("/").reverse().join("-"))
          )
        case "status": {
          const statusOrder = {
            Active: 0,
            Pending: 1,
            PendingLeave: 2,
            Inactive: 3,
            Rejected: 4,
            unknown: 5,
          }
          return statusOrder[a.status] - statusOrder[b.status]
        }
        case "type":
          return a.typeLabel.localeCompare(b.typeLabel)
        default:
          return 0
      }
    })

    console.log("Filtered associations:", filtered)
    return filtered
  }, [associations, filters])

  const handleLeaveAssociation = association => {
    console.log("Opening leave modal for association:", association.id)
    setLeaveModal({ visible: true, association })
  }

  const confirmLeave = async values => {
    const { reason } = values
    const associationId = leaveModal.association?.id
    if (!associationId) return

    try {
      setLoading(true)
      const res = await MemberService.leaveRequest({
        Reason: reason,
        AssociationID: associationId,
      })

      if (res?.StatusCode === 200 && res?.Status === 0) {
        notice({
          msg: "Yêu cầu rời hội đã được gửi. Vui lòng chờ phê duyệt.",
          isSuccess: true,
        })

        const pendingLeaveLabel = mapSystemValue(logAssociationTypeList, 4)

        setAssociations(prev =>
          prev.map(assoc =>
            assoc.id?.toString() === associationId?.toString()
              ? {
                  ...assoc,
                  status: "PendingLeave",
                  statusLabel: pendingLeaveLabel || "Chờ rời hội",
                  leaveReason: reason || "Không có lý do cụ thể",
                }
              : assoc,
          ),
        )

        setLeaveModal({ visible: false, association: null })
        form.resetFields()
      } else {
        const msg =
          res?.Message ||
          (res?.StatusCode === 400
            ? "Bạn không ở trong hội này hoặc đã rời hội!"
            : "Không thể gửi yêu cầu. Vui lòng thử lại.")
        notice({
          msg: "Thất bại",
          desc: msg,
          isSuccess: false,
        })
        setLeaveModal({ visible: false, association: null })
        form.resetFields()
      }
    } catch (error) {
      notice({
        msg: "Lỗi",
        desc: "Có lỗi xảy ra, vui lòng thử lại",
        isSuccess: false,
      })
      setLeaveModal({ visible: false, association: null })
      form.resetFields()
    } finally {
      setLoading(false)
    }
  }

  const handleRevokeLeave = association => {
    if (!association?.id) return

    CB1({
      title: `Bạn có chắc chắn muốn thu hồi yêu cầu rời hội này không?`,
      icon: "question-circle",
      okText: "Đồng ý",
      cancelText: "Hủy",
      onOk: async close => {
        try {
          setLoading(true)
          const res = await MemberService.cancelLeaveRequest([
            association.ObjectID,
          ])

          if (res?.StatusCode === 200 && res?.Status === 0) {
            notice({
              msg: "Thu hồi thành công",
              desc: res.Object || "Yêu cầu đã được thu hồi",
              isSuccess: true,
            })

            const activeLabel = mapSystemValue(logAssociationTypeList, 1)

            setAssociations(prev =>
              prev.map(item =>
                item.id === association.id
                  ? {
                      ...item,
                      status: "Active",
                      statusLabel: activeLabel || "Đang hoạt động",
                      leaveReason: null,
                    }
                  : item,
              ),
            )
            setReasonModal({ visible: false, association: null })
          } else {
            notice({
              msg: "Thu hồi thất bại",
              desc: res?.Message || "Không thể thu hồi. Vui lòng thử lại.",
              isSuccess: false,
            })
          }
        } catch (err) {
          console.error("Revoke error:", err)
          notice({
            msg: "Lỗi hệ thống",
            desc: "Có lỗi xảy ra, vui lòng thử lại.",
            isSuccess: false,
          })
        } finally {
          setLoading(false)
          close()
        }
      },
    })
  }

  const showLeaveReason = association => {
    console.log("Opening reason modal for association:", association.id)
    setReasonModal({ visible: true, association })
  }

  return (
    <div className="association-memberships">
      <Modal
        title="Lý do rời khỏi hiệp hội"
        open={leaveModal.visible}
        onCancel={() => {
          console.log("Cancel modal")
          setLeaveModal({ visible: false, association: null })
          form.resetFields()
        }}
        onOk={() => {
          console.log("Submit button clicked")
          form.submit()
        }}
        okText="Xác nhận"
        cancelText="Hủy"
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={values => {
            console.log("Form submitted with values:", values)
            confirmLeave(values)
          }}
          initialValues={{ reason: "" }}
        >
          <Form.Item
            name="reason"
            rules={[{ required: true, message: "Vui lòng nhập lý do" }]}
          >
            <TextArea rows={4} placeholder="Nhập lý do rời khỏi hiệp hội..." />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title="Lý do rời khỏi hiệp hội"
        open={reasonModal.visible}
        onCancel={() => {
          console.log("Close reason modal")
          setReasonModal({ visible: false, association: null })
        }}
        footer={[
          <Button
            key="revoke"
            type="default"
            danger
            onClick={() => handleRevokeLeave(reasonModal.association)}
          >
            Thu hồi
          </Button>,
          <Button
            key="close"
            type="primary"
            onClick={() => {
              console.log("Close reason modal via button")
              setReasonModal({ visible: false, association: null })
            }}
          >
            Đóng
          </Button>,
        ]}
      >
        <Paragraph>
          {associations.find(item => item.id === reasonModal.association?.id)
            ?.leaveReason || "Không có lý do"}
        </Paragraph>
      </Modal>
      <TitleCustom className="d-flex-sb">
        <span>Danh sách hội tham gia</span>
      </TitleCustom>
      {filteredAssociations.length > 0 ? (
        <Row gutter={[16, 16]} className="associations-grid">
          {filteredAssociations.map(association => {
            const statusConfig = getStatusConfig(association.status)
            return (
              <Col xs={24} sm={12} lg={8} key={association.id}>
                <Card
                  className={`association-card ${statusConfig.className} type-${association.typeCode}`}
                  hoverable
                >
                  <div className="card-header">
                    <div className="association-info">
                      <Avatar
                        size={48}
                        src={association.avatar || undefined}
                        className="association-avatar"
                      >
                        {association.name?.charAt(0) || "?"}
                      </Avatar>
                      <div className="association-details">
                        <Title level={5} className="association-name">
                          {association.name}
                        </Title>
                      </div>
                    </div>
                  </div>
                  <Divider />
                  <div className="association-details-section">
                    <div className="detail-item">
                      <div className="detail-icon">
                        <TeamOutlined />
                      </div>
                      <div className="detail-content">
                        <Text className="detail-label">LOẠI HỘI</Text>
                        <Text className="detail-value">
                          {association.typeLabel}
                        </Text>
                      </div>
                    </div>
                    <div className="detail-item">
                      <div className="detail-icon">
                        <BankOutlined />
                      </div>
                      <div className="detail-content">
                        <Text className="detail-label">HỘI TRỰC THUỘC</Text>
                        <Text className="detail-value">
                          {association.parentAssociation}
                        </Text>
                      </div>
                    </div>
                    <div className="detail-item">
                      <div className="detail-icon">
                        <CalendarOutlined />
                      </div>
                      <div className="detail-content">
                        <Text className="detail-label">NGÀY THAM GIA</Text>
                        <Text className="detail-value">
                          {association.membershipDate}
                        </Text>
                      </div>
                    </div>
                    <div className="detail-item">
                      <div className="detail-icon">{statusConfig.icon}</div>
                      <div className="detail-content">
                        <Text className="detail-label">TRẠNG THÁI</Text>
                        <Text className="detail-value">
                          {association.statusLabel}
                        </Text>
                      </div>
                    </div>
                  </div>
                  <Divider />
                  <div className="card-actions">
                    {association.status === "Active" && (
                      <Button
                        type="primary"
                        danger
                        icon={<LogoutOutlined />}
                        block
                        onClick={() => handleLeaveAssociation(association)}
                      >
                        Rời hội
                      </Button>
                    )}
                    {association.status === "Pending" && (
                      <div className="pending-message">
                        <Text type="warning">Đang chờ phê duyệt</Text>
                        <Text type="secondary" className="sub-message">
                          Vui lòng chờ xác nhận từ hiệp hội
                        </Text>
                      </div>
                    )}
                    {association.status === "PendingLeave" &&
                      association.leaveReason && (
                        <Button
                          type="default"
                          icon={<MessageOutlined />}
                          onClick={() => showLeaveReason(association)}
                          block
                        >
                          Xem lý do rời hội
                        </Button>
                      )}

                    {association.status === "Inactive" &&
                      association.leaveReason && (
                        <Button
                          type="default"
                          icon={<MessageOutlined />}
                          onClick={() => showLeaveReason(association)}
                          block
                        >
                          Xem lý do rời hội
                        </Button>
                      )}
                    {association.status === "Rejected" && (
                      <Text type="danger">Đã bị từ chối tham gia</Text>
                    )}
                  </div>
                </Card>
              </Col>
            )
          })}
        </Row>
      ) : (
        <Empty
          description={
            <span>
              {filters.search
                ? `Không tìm thấy hiệp hội nào chứa "${filters.search}".`
                : filters.status !== "all" || filters.type !== "all"
                ? "Không có hiệp hội nào phù hợp với bộ lọc hiện tại."
                : "Danh sách hiệp hội trống."}
              <br />
              {filters.search ||
              filters.status !== "all" ||
              filters.type !== "all" ? (
                <Button
                  type="link"
                  onClick={() =>
                    setFilters({
                      search: "",
                      status: "all",
                      type: "all",
                      sort: "name",
                    })
                  }
                >
                  Xóa bộ lọc
                </Button>
              ) : null}
            </span>
          }
          className="empty-state"
        />
      )}
    </div>
  )
}

export default JoinedOrganizations
